export const lifeCarePlanCategoryConfig = {
    therapies: {
        title: 'Therapies',
        columns: [
            {
                field: 'name',
                headerName: 'Therapy Name',
                type: 'text',
                required: true,
                helperText: "Type of therapy service (e.g., Physical Therapy).",
                reviewHeader: 'Therapy Name',
                reportHeader: 'Therapy Name',
            },
            {
                field: 'startDate',
                headerName: 'Start Date',
                type: 'date',
                required: true,
                helperText: "Date when this therapy service begins.",
                reviewHeader: 'Start',
                reportHeader: 'Start Date',
            },
            {
                field: 'endDate',
                headerName: 'End Date',
                type: 'date',
                required: true,
                helperText: "Date when this therapy service is expected to end.",
                reviewHeader: 'End',
                reportHeader: 'End Date',
            },
            {
                field: 'quantity',
                headerName: 'Quantity',
                type: 'number',
                min: 0,
                required: true,
                helperText: "Units per session (e.g., 1).",
                align: 'right',
                reviewHeader: 'Qty',
            },
            {
                field: 'frequencyPerYear',
                headerName: 'Freq/Year',
                type: 'number',
                min: 0,
                required: true,
                helperText: "Number of times per year the therapy is received.",
                align: 'right',
                reviewHeader: 'Freq/Yr',
                reportHeader: 'Freq/Year',
            },
            {
                field: 'costPerTreatment',
                headerName: 'Cost/Treatment',
                type: 'number',
                adornment: '$',
                min: 0,
                isCurrency: true,
                required: true,
                helperText: "Cost of a single therapy session.",
                align: 'right',
                reviewHeader: 'Cost/Tx',
                reportHeader: 'Cost/Treatment',
            },
            {
                field: 'annualCost',
                headerName: 'Annual Cost',
                type: 'text',
                adornment: '$',
                min: 0,
                isCurrency: true,
                readOnly: true,
                helperText: "Annual cost for this therapy, based on frequency and unit cost.",
                align: 'right',
                reviewHeader: 'Annual Cost',
                reportHeader: 'Annual Cost',
            },
        ],
    },
    medicalCare: {
        title: 'Medical Care & Equipment',
        columns: [
            {
                field: 'name',
                headerName: 'Care/Item Name',
                type: 'text',
                required: true,
                helperText: "Type of medical care (e.g., Pain Management Consultation).",
                reviewHeader: 'Care/Item Name',
                reportHeader: 'Care/Item Name',
            },
            {
                field: 'startDate',
                headerName: 'Start Date',
                type: 'date',
                required: true,
                helperText: "Date when this medical care begins.",
                reviewHeader: 'Start',
                reportHeader: 'Start Date',
            },
            {
                field: 'endDate',
                headerName: 'End Date',
                type: 'date',
                required: true,
                helperText: "Date when this care ends.",
                reviewHeader: 'End',
                reportHeader: 'End Date',
            },
            {
                field: 'quantity',
                headerName: 'Quantity',
                type: 'number',
                min: 0,
                required: true,
                helperText: "Units per visit (typically 1).",
                align: 'right',
                reviewHeader: 'Qty',
            },
            {
                field: 'frequencyPerYear',
                headerName: 'Freq/Year',
                type: 'number',
                min: 0,
                required: true,
                helperText: "How many times per year the service occurs.",
                align: 'right',
                reviewHeader: 'Freq/Yr',
                reportHeader: 'Freq/Year',
            },
            {
                field: 'costPerTreatment',
                headerName: 'Cost/Item',
                type: 'number',
                adornment: '$',
                min: 0,
                isCurrency: true,
                required: true,
                helperText: "Cost per medical visit.",
                align: 'right',
                reviewHeader: 'Cost/Item',
                reportHeader: 'Cost/Item',
            },
            {
                field: 'annualCost',
                headerName: 'Annual Cost',
                type: 'text',
                adornment: '$',
                min: 0,
                isCurrency: true,
                readOnly: true,
                helperText: "Annual cost for this medical care.",
                align: 'right',
                reviewHeader: 'Annual Cost',
                reportHeader: 'Annual Cost',
            },
        ],
    },
    nursingCare: {
        title: 'Nursing/Attendant Care',
        columns: [
            {
                field: 'name',
                headerName: 'Care Type',
                type: 'text',
                required: true,
                helperText: "Nursing service type (e.g., Attendant Care, Skilled Nursing).",
                reviewHeader: 'Care Type',
                reportHeader: 'Care Type',
            },
            {
                field: 'startDate',
                headerName: 'Start Date',
                type: 'date',
                required: true,
                helperText: "Start date of nursing care.",
                reviewHeader: 'Start',
                reportHeader: 'Start Date',
            },
            {
                field: 'endDate',
                headerName: 'End Date',
                type: 'date',
                required: true,
                helperText: "End date of nursing care.",
                reviewHeader: 'End',
                reportHeader: 'End Date',
            },
            {
                field: 'quantity',
                headerName: 'Quantity',
                type: 'number',
                min: 0,
                required: true,
                helperText: "Units per time frame (e.g., 2 hours).",
                align: 'right',
                reviewHeader: 'Qty',
            },
            {
                field: 'frequency',
                headerName: 'Frequency',
                type: 'text',
                select: true,
                options: ["Per Day", "Per Week", "Per Month", "Per Year"],
                required: true,
                helperText: "Time frequency of the service (e.g., Per Day, Per Week).",
                reviewHeader: 'Freq',
                reportHeader: 'Frequency',
            },
            {
                field: 'costPerUnit',
                headerName: 'Cost/Unit',
                type: 'number',
                adornment: '$',
                min: 0,
                isCurrency: true,
                required: true,
                helperText: "Cost for one unit of service.",
                align: 'right',
                reviewHeader: 'Cost/Unit',
                reportHeader: 'Cost/Unit',
            },
            {
                field: 'annualCost',
                headerName: 'Annual Cost',
                type: 'text',
                adornment: '$',
                min: 0,
                isCurrency: true,
                readOnly: true,
                helperText: "Annual cost of the nursing service.",
                align: 'right',
                reviewHeader: 'Annual Cost',
                reportHeader: 'Annual Cost',
            },
        ],
    },
    pharmacy: {
        title: 'Pharmacy',
        columns: [
            {
                field: 'name',
                headerName: 'Medication',
                type: 'text',
                required: true,
                helperText: "Medication name and dosage (e.g., Cyclobenzaprine 0mg).",
                reviewHeader: 'Medication',
                reportHeader: 'Medication',
            },
            {
                field: 'startDate',
                headerName: 'Start Date',
                type: 'date',
                required: true,
                helperText: "Start date of medication use.",
                reviewHeader: 'Start',
                reportHeader: 'Start Date',
            },
            {
                field: 'endDate',
                headerName: 'End Date',
                type: 'date',
                required: true,
                helperText: "End date of medication use.",
                reviewHeader: 'End',
                reportHeader: 'End Date',
            },
            {
                field: 'quantity',
                headerName: 'Quantity',
                type: 'number',
                min: 0,
                required: true,
                helperText: "Units per dose.",
                align: 'right',
                reviewHeader: 'Qty',
            },
            {
                field: 'frequencyPerWeek',
                headerName: 'Freq/Week',
                type: 'number',
                min: 0,
                required: true,
                helperText: "How many times per week the medication is taken.",
                align: 'right',
                reviewHeader: 'Freq/Wk',
                reportHeader: 'Freq/Week',
            },
            {
                field: 'costPerUnit',
                headerName: 'Cost/Unit',
                type: 'number',
                adornment: '$',
                min: 0,
                isCurrency: true,
                required: true,
                helperText: "Cost per unit (e.g., per pill).",
                align: 'right',
                reviewHeader: 'Cost/Unit',
                reportHeader: 'Cost/Unit',
            },
            {
                field: 'annualCost',
                headerName: 'Annual Cost',
                type: 'text',
                adornment: '$',
                min: 0,
                isCurrency: true,
                readOnly: true,
                helperText: "Estimated annual cost for the medication.",
                align: 'right',
                reviewHeader: 'Annual Cost',
                reportHeader: 'Annual Cost',
            },
        ],
    },
};